import * as Yup from 'yup';
import { v4 as uuidV4 } from 'uuid';

import { onlyLetters, minCharacters } from 'utils/yupValidations';
import {
	convertDate,
	formatDateISOWithOutUTC,
	limparMascara,
	unformatDate,
} from 'utils/genericFunctions';
import { comparaData } from 'utils/dateFunctions';

import {
	DeficienciaCadastroFichaAluno,
	FichaAlunoEnvio,
	RecursoAvaliacaoCadastroFichaAluno,
} from 'store/modules/api/educacaoUpdate/fichaAluno/cadastrarFichaAluno/types';
import {
	AlterarFichaAlunoRequestData,
	AlterarFichaAlunoTelefone,
} from 'store/modules/api/educacaoUpdate/fichaAluno/alterarFichaAluno/types';
import {
	ConsultaFichaAlunoData,
	Irmao,
	ListaNecessidadesEspeciais,
	Telefone,
} from 'store/modules/api/educacaoUpdate/fichaAluno/consultaFichaAluno/types';

// import { trimValues } from 'utils/stringFunctions';

export interface IFichaAluno {
	aluno: Aluno;
	idProcesso: number;
	mensagem: string;
	dataAlteracao: string;
	dadosPessoais: IDadosPessoais;
	documentos: IDocumentos;
	justificativaDocumentos?: string;
	certidaoAntiga: ICertidaoAntiga;
	certidaoNova: ICertidaoNova;
	enderecoResidencial: IEnderecoResidencial;
	telefones: ITelefone[];
	telefone: ITelefone;
	deficiencia: IDeficiencia;
	recursoAvaliacao: IRecursoAvaliacao;
	listaNecessidadesEspeciais: ListaNecessidadesEspeciais[];
	irmaos: IIrmao[];
	indigena?: Indigena;
}

interface Aluno {
	numeroRA: string;
	digitoRA: string;
	siglaUFRA: string;
}

export interface IDadosPessoais {
	numeroRA: string;
	digitoRA: string;
	siglaUFRA: string;
	nomeAluno: string;
	dataNascimento: string;
	corRaca: number | string;
	descricaoCorRaca: string;
	codigoSexo: number | string;
	sexo: string | number;
	nomeMae: string;
	nomePai: string;
	nomeSocial: string;
	nomeAfetivo: string;
	email: string;
	nacionalidade: number | string;
	descricaoNacionalidade: string;
	codigoPaisOrigem: number | string;
	nomePaisOrigem: string;
	dataEntradaPais: string;
	bolsaFamilia: string;
	quilombola: string;
	nomeMunicipioNascimento: string;
	ufMunicipioNascimento: string;
	alunoFalecido: string;
	dataFaleciomento: string;
	codigoMunicipioNascimentoDNE: number | string;
	codigoBolsaFamilia: number | boolean | string;
	possuiInternet: string;
	possuiNotebookSmartphoneTablet: string;
	descTipoIndigena?: string;
	codTipoIndigena: string | number;
}

export interface IFormDadosPessoais {
	alunoFalecido: string;
	codigoBolsaFamilia: number | boolean;
	codigoMunicipioNascimentoDNE: number;
	codigoPaisOrigem: number;
	codigoSexo: number;
	corRaca: number;
	dataNascimento: string;
	descricaoCorRaca: string;
	descricaoNacionalidade: string;
	digitoRA: string;
	nacionalidade: number;
	nomeAluno: string;
	nomeMae: string;
	nomeMunicipioNascimento: string;
	nomePai: string;
	nomePaisOrigem: string;
	numeroRA: string;
	sexo: string;
	siglaUFRA: string;
	ufMunicipioNascimento: string;
	quilombola: string;
	possuiInternet: string;
	possuiNotebookSmartphoneTablet: string;
}

interface IDocumentos {
	codigoINEP: number | string;
	cpf: string;
	numeroDocumentoCivil: string;
	digitoDocumentoCivil: string;
	ufDocumentoCivil: string;
	dataEmissaoDocumentoCivil: string;
	dataEmissaoCertidao: string;
	tipoCertidao: string;
	numeroNIS: string;
	numeroRG: string;
	digitoRG: string;
	ufRG: string;
	justificativaDocumentos: string;
	dataEmissaoRG: string;
	isCin: boolean;
	dataEmissaoCin: string; // "2025-01-01"
}

interface ICertidaoAntiga {
	numeroCertidao: string;
	numeroLivroRegistro: string;
	numeroFolhaRegistro: string;
	nomeMunicipioComarca: string;
	ufComarca: string;
	distritoNascimento: string;
	// Envio
	folha: string;
	livro: string;
	municipioComarca: string;
	distritoCertidao: string;
	dataEmissaoCertidao: string;
	tipoCertidao: string;
}

interface ICertidaoNova {
	certificadoMatricula01: string;
	certificadoMatricula02: string;
	certificadoMatricula03: string;
	certificadoMatricula04: string;
	certificadoMatricula05: string;
	certificadoMatricula06: string;
	certificadoMatricula07: string;
	certificadoMatricula08: string;
	certificadoMatricula09: string;
	dataEmissaoCertidao?: string;
	tipoCertidao: string;
}

interface IEnderecoResidencial {
	logradouro: string;
	numero: string;
	areaLogradouro: string;
	complemento: string;
	bairro: string;
	nomeCidade: string;
	ufCidade: string;
	latitude: string;
	longitude: string;
	cep: string;
	codigoMunicipioDNE: string | number;
	codigoArea: number | string;
	codigoLocalizacao: number | string;
	localizacaoDiferenciada: string;
}

export interface ITelefone {
	id?: string;
	ddd: string;
	numero: string;
	tipo: number;
	descricaoTipo: string | number;
	complemento: string;
	sms: string;
}

interface ITelefones {
	telefones: ITelefone[];
}

interface IDeficiencia {
	codigo: string | boolean;
	descricao: string | boolean;
	codigoNecessidade: string;
	mobilidadeReduzida: string | boolean;
	tipoMobilidadeReduzida?: string;
	dataInicioMobilidadeReduzida?: string;
	dataFimMobilidadeReduzida?: string;
	tipoMobilidadeReduxida?: string;
	// professorAuxiliar?: string | boolean;
	// dataInicioProfessorAuxiliar?: string;
	// dataFimProfessorAuxiliar?: string;
	cuidador: string | number;
	tipoCuidador: string;
	profSaude: string | number;
	tipoProfSaude: string;
	possuiNecessidades: string | boolean;
	codigoNivelSuporte: string | number;
	nivelSuporte?: boolean;
	profissionalApoioEscolarVO: {
		profissionalApoioEscolar: boolean;
		atividadeDiaria: boolean;
		atividadeEscolar: boolean;
		apoioHigiene: boolean;
		apoioLocomocao: boolean;
		apoioAlimentacao: boolean;
		apoioBanheiro: boolean;
		apoioMedicamento: boolean;
		dataInicioVigenciaAtividadeDiaria: string;
		dataFimVigenciaAtividadeDiaria: string;
		dataInicioVigenciaAtividadeEscolar: string;
		dataFimVigenciaAtividadeEscolar: string;
	};
}

interface IRecursoAvaliacao {
	tamanhoFonte: number | string;
	guiaInterprete: boolean;
	interpreteLibras: boolean;
	leituraLabial: boolean;
	nenhum: boolean;
	provaAmpliada: boolean;
	fonteProva: string;
	provaBraile: boolean;
	auxilioTranscricao: boolean;
	auxilioLeitor: boolean;
	provaVideoLibras: boolean;
	provaLinguaPortuguesa: boolean;
	cdAudioDeficienteVisual: boolean;
}

interface INecessidadesEspeciais {
	codigo: number;
	nome: string;
	tipo?: string;
	lista?: [];
}

interface IIrmao {
	nomeAluno: string;
	dataNascimento: string;
	numeroRA: string;
	digitoRA: string;
	siglaUFRA: string;
	gemeo: string;
}

interface Indigena {
	codTipoIndigena: string;
	tipoIndigena: string;
}

export const initialValuesTelefone: ITelefones = {
	telefones: [],
};

export const initialValues: IFichaAluno = {
	aluno: {
		numeroRA: '',
		digitoRA: '',
		siglaUFRA: '',
	},
	idProcesso: 0,
	mensagem: '',
	dataAlteracao: '',
	dadosPessoais: {
		numeroRA: '',
		digitoRA: '',
		siglaUFRA: '',
		nomeAluno: '',
		dataNascimento: '',
		corRaca: '',
		descricaoCorRaca: '',
		codigoSexo: '',
		sexo: '',
		nomeMae: '',
		nomePai: '',
		nomeSocial: '',
		nomeAfetivo: '',
		email: '',
		nacionalidade: '',
		descricaoNacionalidade: '',
		codigoPaisOrigem: '',
		nomePaisOrigem: '',
		dataEntradaPais: '',
		bolsaFamilia: '',
		quilombola: '',
		nomeMunicipioNascimento: '',
		ufMunicipioNascimento: '',
		alunoFalecido: '',
		dataFaleciomento: '',
		codigoMunicipioNascimentoDNE: '',
		codigoBolsaFamilia: '',
		possuiInternet: '',
		possuiNotebookSmartphoneTablet: '',
		descTipoIndigena: '',
		codTipoIndigena: '',
	},
	documentos: {
		codigoINEP: '',
		cpf: '',
		numeroDocumentoCivil: '',
		digitoDocumentoCivil: '',
		ufDocumentoCivil: '',
		dataEmissaoDocumentoCivil: '',
		dataEmissaoCertidao: '',
		tipoCertidao: '',
		numeroNIS: '',
		numeroRG: '',
		digitoRG: '',
		ufRG: '',
		justificativaDocumentos: '',
		dataEmissaoRG: '',
		isCin: false,
		dataEmissaoCin: '',
	},
	justificativaDocumentos: '',
	certidaoAntiga: {
		numeroCertidao: '',
		numeroLivroRegistro: '',
		numeroFolhaRegistro: '',
		nomeMunicipioComarca: '',
		ufComarca: '',
		distritoNascimento: '',
		folha: '',
		livro: '',
		municipioComarca: '',
		distritoCertidao: '',
		dataEmissaoCertidao: '',
		tipoCertidao: '',
	},
	certidaoNova: {
		certificadoMatricula01: '',
		certificadoMatricula02: '',
		certificadoMatricula03: '',
		certificadoMatricula04: '',
		certificadoMatricula05: '',
		certificadoMatricula06: '',
		certificadoMatricula07: '',
		certificadoMatricula08: '',
		certificadoMatricula09: '',
		dataEmissaoCertidao: '',
		tipoCertidao: '',
	},
	enderecoResidencial: {
		logradouro: '',
		numero: '',
		areaLogradouro: '',
		complemento: '',
		bairro: '',
		nomeCidade: '',
		ufCidade: 'SP',
		latitude: '',
		longitude: '',
		cep: '',
		codigoMunicipioDNE: '',
		codigoArea: '',
		codigoLocalizacao: '',
		localizacaoDiferenciada: '',
	},
	telefones: [],
	telefone: {
		id: '',
		complemento: '',
		ddd: '',
		descricaoTipo: '',
		numero: '',
		sms: '',
		tipo: 0,
	},
	deficiencia: {
		codigo: '',
		codigoNecessidade: '',
		descricao: '',
		mobilidadeReduzida: '',
		tipoMobilidadeReduzida: '',
		tipoMobilidadeReduxida: '',
		cuidador: '',
		tipoCuidador: '',
		profSaude: '',
		tipoProfSaude: '',
		possuiNecessidades: false,
		codigoNivelSuporte: '0',
		profissionalApoioEscolarVO: {
			profissionalApoioEscolar: false,
			atividadeDiaria: false,
			atividadeEscolar: false,
			apoioHigiene: false,
			apoioLocomocao: false,
			apoioAlimentacao: false,
			apoioBanheiro: false,
			apoioMedicamento: false,
			dataInicioVigenciaAtividadeDiaria: '', // 2022-01-01
			dataFimVigenciaAtividadeDiaria: '', // 2022-01-01
			dataInicioVigenciaAtividadeEscolar: '', // 2022-01-01
			dataFimVigenciaAtividadeEscolar: '', // 2022-01-01
		},
	},
	recursoAvaliacao: {
		guiaInterprete: false,
		interpreteLibras: false,
		leituraLabial: false,
		nenhum: false,
		provaAmpliada: false,
		tamanhoFonte: '',
		fonteProva: '',
		provaBraile: false,
		auxilioTranscricao: false,
		auxilioLeitor: false,
		provaVideoLibras: false,
		cdAudioDeficienteVisual: false,
		provaLinguaPortuguesa: false,
	},
	listaNecessidadesEspeciais: [],
	irmaos: [],
	// indigena: {
	// 	codTipoIndigena: '',
	// 	tipoIndigena: '',
	// },
};

export const initialValuesDadosCadastrais: IDadosPessoais = {
	numeroRA: '',
	digitoRA: '',
	siglaUFRA: '',
	nomeAluno: '',
	dataNascimento: '',
	corRaca: 0,
	descricaoCorRaca: '',
	codigoSexo: 0,
	sexo: '',
	nomeMae: '',
	nomePai: '',
	nomeSocial: '',
	nomeAfetivo: '',
	email: '',
	nacionalidade: 0,
	descricaoNacionalidade: '',
	codigoPaisOrigem: 0,
	nomePaisOrigem: '',
	dataEntradaPais: '',
	bolsaFamilia: '',
	quilombola: '',
	nomeMunicipioNascimento: '',
	ufMunicipioNascimento: '',
	alunoFalecido: '',
	dataFaleciomento: '',
	codigoMunicipioNascimentoDNE: 0,
	codigoBolsaFamilia: 0,
	possuiInternet: '',
	possuiNotebookSmartphoneTablet: '',
	codTipoIndigena: '',
	descTipoIndigena: '',
};

export const treatSpecialNeeds = (
	values: ListaNecessidadesEspeciais[],
): ListaNecessidadesEspeciais[] => {
	const formattedSpecialNeeds: ListaNecessidadesEspeciais[] = [];

	if (values.length > 0) {
		values.map((item: ListaNecessidadesEspeciais) => {
			formattedSpecialNeeds.push({
				codigoNecessidadeEspecial: item.codigoNecessidadeEspecial || '0',
				nomeNecessidadeEspecial: item.nomeNecessidadeEspecial || '',
			});

			return null;
		});

		return formattedSpecialNeeds;
	}

	return [];
};

export const treatBrother = (values: Irmao[]): IIrmao[] => {
	const formattedBrotherList: IIrmao[] = [];

	if (values.length > 0) {
		values.map((item: Irmao) => {
			formattedBrotherList.push({
				nomeAluno: item.nomeAluno,
				dataNascimento: item.dataNascimento,
				numeroRA: item.numeroRA,
				digitoRA: item.digitoRA,
				siglaUFRA: item.siglaUFRA,
				gemeo: item.gemeo ? 'S' : 'N',
			});

			return null;
		});

		return formattedBrotherList;
	}

	return [];
};

export const treatPhoneData = (values: Telefone[]): ITelefone[] => {
	const formattedPhoneList: ITelefone[] = [];
	if (values.length > 0) {
		values.map((item: Telefone) => {
			const {
				dddNumero,
				numero,
				tipoTelefone,
				descTipoTelefone,
				complemento,
				sms,
			} = item;

			formattedPhoneList.push({
				id: uuidV4(),
				ddd: dddNumero,
				numero,
				tipo: Number(tipoTelefone) || 0,
				descricaoTipo: descTipoTelefone,
				complemento: complemento === '' ? '' : complemento,
				sms: sms ? 'SIM' : 'NAO',
			});

			return null;
		});

		return formattedPhoneList;
	}

	return [];
};

export const treatInitialValues = ({
	documentos,
	enderecoResidencial,
	deficiencia,
	recursoAvaliacao,
	processoId,
	sucesso,
	dataAlteracaoFicha,
	dadosPessoais,
	justificativaDocumentos,
	certidaoAntiga,
	certidaoNova,
	irmaos,
	listaNecessidadesEspeciais,
	telefones,
	indigena,
}: ConsultaFichaAlunoData): IFichaAluno => {
	const values = {
		telefone: initialValues.telefone,
		listaNecessidadesEspeciais: listaNecessidadesEspeciais
			? treatSpecialNeeds(listaNecessidadesEspeciais)
			: [],
		irmaos: irmaos ? treatBrother(irmaos) : [],
		telefones: telefones ? treatPhoneData(telefones) : [],
		idProcesso: Number(processoId) || 0,
		mensagem: sucesso || '',
		dataAlteracao:
			(dataAlteracaoFicha && formatDateISOWithOutUTC(dataAlteracaoFicha)) || '',
		justificativaDocumentos: justificativaDocumentos || '',
		aluno: {
			numeroRA: dadosPessoais?.numRA?.trim() || '',
			digitoRA: dadosPessoais?.digitoRA?.trim() || '',
			siglaUFRA: dadosPessoais?.siglaUFRA || '',
		},
		certidaoNova: {
			certificadoMatricula01: certidaoNova?.certMatr01 || '',
			certificadoMatricula02: certidaoNova?.certMatr02 || '',
			certificadoMatricula03: certidaoNova?.certMatr03 || '',
			certificadoMatricula04: certidaoNova?.certMatr04 || '',
			certificadoMatricula05: certidaoNova?.certMatr05 || '',
			certificadoMatricula06: certidaoNova?.certMatr06 || '',
			certificadoMatricula07: certidaoNova?.certMatr07 || '',
			certificadoMatricula08: certidaoNova?.certMatr08 || '',
			certificadoMatricula09: certidaoNova?.certMatr09 || '',
			dataEmissaoCertidao:
				(certidaoNova?.certMatr01 &&
					documentos.dataEmissaoCertidao &&
					formatDateISOWithOutUTC(documentos?.dataEmissaoCertidao)) ||
				'',
			tipoCertidao: '',
		},
		certidaoAntiga: {
			numeroCertidao: certidaoAntiga?.numCertidao || '',
			numeroLivroRegistro: certidaoAntiga?.livro || '',
			numeroFolhaRegistro: certidaoAntiga?.folha || '',
			nomeMunicipioComarca: certidaoAntiga?.municipioComarca || '',
			distritoNascimento: certidaoAntiga?.distritoCertidao || '',
			folha: certidaoAntiga?.folha || '',
			livro: certidaoAntiga?.livro || '',
			municipioComarca: certidaoAntiga?.municipioComarca || '',
			distritoCertidao: certidaoAntiga?.distritoCertidao || '',
			tipoCertidao: '',
			dataEmissaoCertidao:
				(certidaoAntiga?.numCertidao &&
					documentos?.dataEmissaoCertidao &&
					formatDateISOWithOutUTC(documentos?.dataEmissaoCertidao)) ||
				'',
			ufComarca: certidaoAntiga?.ufComarca || '',
		},
		dadosPessoais: {
			numeroRA: dadosPessoais?.numRA?.trim() || '',
			digitoRA: dadosPessoais?.digitoRA?.trim() || '',
			siglaUFRA: dadosPessoais?.siglaUFRA || '',
			nomeAluno: dadosPessoais?.nomeAluno || '',
			dataNascimento:
				(dadosPessoais?.dataNascimento &&
					formatDateISOWithOutUTC(dadosPessoais?.dataNascimento)) ||
				'',
			corRaca: Number(dadosPessoais?.codCorRaca) || 0,
			descricaoCorRaca: dadosPessoais?.corRaca || '',
			codigoSexo: Number(dadosPessoais?.codSexo) || 0,
			sexo: dadosPessoais?.sexo?.toString() || '',
			nomeMae: dadosPessoais?.nomeMae || '',
			nomePai: dadosPessoais?.nomePai || '',
			nomeSocial: dadosPessoais?.nomeSocial || '',
			nomeAfetivo: dadosPessoais?.nomeAfetivo || '',
			email: dadosPessoais?.email || '',
			nacionalidade: Number(dadosPessoais?.codNacionalidade) || 0,
			descricaoNacionalidade: dadosPessoais?.descricaoNacionalidade || '',
			codigoPaisOrigem: dadosPessoais?.codPaisOrigem,
			nomePaisOrigem: dadosPessoais?.paisOrigem || '',
			dataEntradaPais:
				(dadosPessoais?.dataEntradaPais &&
					formatDateISOWithOutUTC(dadosPessoais?.dataEntradaPais)) ||
				'',
			bolsaFamilia: dadosPessoais?.bolsaFamilia ? 'SIM' : '',
			quilombola: dadosPessoais?.quilombola ? 'SIM' : '',
			nomeMunicipioNascimento: dadosPessoais?.nomeMunicipioNascimento || '',
			ufMunicipioNascimento: dadosPessoais?.ufMunicipioNascimento || '',
			alunoFalecido: dadosPessoais?.alunoFalecido ? 'SIM' : '',
			dataFaleciomento:
				(dadosPessoais?.dataFalecimento &&
					formatDateISOWithOutUTC(dadosPessoais?.dataFalecimento)) ||
				'',
			codigoMunicipioNascimentoDNE:
				Number(dadosPessoais?.codMunicipioNascimentoDNE) || 0,
			codigoBolsaFamilia: 0,
			possuiInternet: dadosPessoais?.possuiInternet ? 'SIM' : '',
			possuiNotebookSmartphoneTablet:
				dadosPessoais?.possuiNotebookSmartphoneTablet ? 'SIM' : '',
			codTipoIndigena: indigena?.codTipoIndigena || '',
			descTipoIndigena: indigena?.tipoIndigena || '',
		},
		enderecoResidencial: {
			logradouro: enderecoResidencial?.logradouro || '',
			numero: enderecoResidencial?.numero || '',
			complemento: enderecoResidencial?.complemento || '',
			bairro: enderecoResidencial?.bairro || '',
			nomeCidade: enderecoResidencial?.nomeCidade || '',
			ufCidade: enderecoResidencial?.ufCidade || '',
			// latitude: enderecoResidencial?.latitude || '-23.5489',
			// longitude: enderecoResidencial?.longitude || '-46.6388',
			latitude: enderecoResidencial.latitude
				? enderecoResidencial.latitude.replace(',', '.')
				: '',
			longitude: enderecoResidencial.longitude
				? enderecoResidencial.longitude.replace(',', '.')
				: '',
			cep: enderecoResidencial?.cep || '',
			codigoMunicipioDNE: enderecoResidencial?.codMunicipioDNE || '',
			codigoArea: enderecoResidencial?.codAreaLogradouro || '',
			codigoLocalizacao: enderecoResidencial?.codLocalizacao || '',
			localizacaoDiferenciada:
				enderecoResidencial?.codLocalizacaoDiferenciada || '',
			areaLogradouro: enderecoResidencial?.codAreaLogradouro || '',
		},
		documentos: {
			codigoINEP: documentos?.codigoINEP || '',
			cpf: documentos?.cpf || '',
			numeroDocumentoCivil: documentos?.numDocumentoCivil || '',
			digitoDocumentoCivil: documentos?.digitoDocumentoCivil || '',
			ufDocumentoCivil: documentos?.ufDocumentoCivil || '',
			dataEmissaoDocumentoCivil:
				(documentos?.dataEmissaoDocumentoCivil &&
					formatDateISOWithOutUTC(documentos?.dataEmissaoDocumentoCivil)) ||
				'',
			dataEmissaoCertidao:
				(documentos?.dataEmissaoCertidao &&
					formatDateISOWithOutUTC(documentos?.dataEmissaoCertidao)) ||
				'',
			tipoCertidao: '',
			numeroNIS: documentos?.numeroNis || '',
			numeroRG: documentos?.numDocumentoCivil || '',
			digitoRG: documentos?.digitoDocumentoCivil || '',
			ufRG: documentos?.ufDocumentoCivil || '',
			justificativaDocumentos: justificativaDocumentos || '',
			dataEmissaoRG:
				(documentos?.dataEmissaoDocumentoCivil &&
					formatDateISOWithOutUTC(documentos?.dataEmissaoDocumentoCivil)) ||
				'',
			isCin: documentos?.isCin,
			dataEmissaoCin:
				(documentos?.dataEmissaoCin &&
					formatDateISOWithOutUTC(documentos?.dataEmissaoCin)) ||
				'',
		},
		deficiencia: {
			codigo: '',
			descricao: '',
			codigoNecessidade: '',
			cuidador: '',
			tipoCuidador: '',
			profSaude: '',
			tipoProfSaude: '',
			tipoMobilidadeReduxida: '',
			mobilidadeReduzida: deficiencia?.mobilidadeReduzida || false,
			tipoMobilidadeReduzida: deficiencia?.tipoMobilidadeReduzida || '',
			possuiNecessidades:
				listaNecessidadesEspeciais && listaNecessidadesEspeciais.length !== 0,
			dataInicioMobilidadeReduzida:
				(deficiencia?.dataInicioMobilidadeReduzida &&
					formatDateISOWithOutUTC(deficiencia.dataInicioMobilidadeReduzida)) ||
				'',
			dataFimMobilidadeReduzida:
				(deficiencia?.dataFimMobilidadeReduzida &&
					formatDateISOWithOutUTC(deficiencia.dataFimMobilidadeReduzida)) ||
				'',
			codigoNivelSuporte: deficiencia?.codigoNivelSuporte || '',
			// professorAuxiliar:deficiencia?.dataInicioProfessorAuxiliar || false,
			// dataInicioProfessorAuxiliar: deficiencia?.dataInicioProfessorAuxiliar || '',
			// dataFimProfessorAuxiliar: deficiencia?.dataFimProfessorAuxiliar || '',
			profissionalApoioEscolarVO: {
				profissionalApoioEscolar:
					deficiencia?.profissionalApoioEscolarVO.profissionalApoioEscolar ||
					false,
				atividadeDiaria:
					deficiencia?.profissionalApoioEscolarVO.atividadeDiaria || false,
				atividadeEscolar:
					deficiencia?.profissionalApoioEscolarVO.atividadeEscolar || false,
				apoioHigiene:
					deficiencia?.profissionalApoioEscolarVO.apoioHigiene || false,
				apoioLocomocao:
					deficiencia?.profissionalApoioEscolarVO.apoioLocomocao || false,
				apoioAlimentacao:
					deficiencia?.profissionalApoioEscolarVO.apoioAlimentacao || false,
				apoioBanheiro:
					deficiencia?.profissionalApoioEscolarVO.apoioBanheiro || false,
				apoioMedicamento:
					deficiencia?.profissionalApoioEscolarVO.apoioMedicamento || false,
				dataInicioVigenciaAtividadeDiaria:
					(deficiencia?.profissionalApoioEscolarVO
						.dataInicioVigenciaAtividadeDiaria &&
						formatDateISOWithOutUTC(
							deficiencia.profissionalApoioEscolarVO
								.dataInicioVigenciaAtividadeDiaria,
						)) ||
					'',
				dataFimVigenciaAtividadeDiaria:
					(deficiencia?.profissionalApoioEscolarVO
						.dataFimVigenciaAtividadeDiaria &&
						formatDateISOWithOutUTC(
							deficiencia.profissionalApoioEscolarVO
								.dataFimVigenciaAtividadeDiaria,
						)) ||
					'',
				dataInicioVigenciaAtividadeEscolar:
					(deficiencia?.profissionalApoioEscolarVO
						.dataInicioVigenciaAtividadeEscolar &&
						formatDateISOWithOutUTC(
							deficiencia.profissionalApoioEscolarVO
								.dataInicioVigenciaAtividadeEscolar,
						)) ||
					'',
				dataFimVigenciaAtividadeEscolar:
					(deficiencia?.profissionalApoioEscolarVO
						.dataFimVigenciaAtividadeEscolar &&
						formatDateISOWithOutUTC(
							deficiencia.profissionalApoioEscolarVO
								.dataFimVigenciaAtividadeEscolar,
						)) ||
					'',
			},
			nivelSuporte: deficiencia?.codigoNivelSuporte !== 0,
		},
		recursoAvaliacao: {
			guiaInterprete: !!recursoAvaliacao?.guiaInterprete,
			interpreteLibras: !!recursoAvaliacao?.interpreteLibras,
			leituraLabial: !!recursoAvaliacao?.leituraLabial,
			nenhum: !!recursoAvaliacao?.nenhum,
			provaAmpliada: !!recursoAvaliacao?.provaAmpliada,
			tamanhoFonte: recursoAvaliacao?.tamanhoFonte?.toString() || 0,
			fonteProva18: recursoAvaliacao?.tamanhoFonte?.toString() === '18',
			fonteProva20: recursoAvaliacao?.tamanhoFonte?.toString() === '20',
			fonteProva: recursoAvaliacao?.tamanhoFonte?.toString() || '',
			provaBraile: !!recursoAvaliacao?.provaBraile,
			auxilioTranscricao: !!recursoAvaliacao?.auxilioTranscricao,
			auxilioLeitor: !!recursoAvaliacao?.auxilioLeitor,
			provaVideoLibras: !!recursoAvaliacao?.provaVideoLibras,
			cdAudioDeficienteVisual: !!recursoAvaliacao?.cdAudioDefVisual,
			provaLinguaPortuguesa: !!recursoAvaliacao?.provaLinguaPortuguesa,
		},
	};
	return values;
};

export const schema = Yup.object<IFichaAluno>().shape({
	// DADOS PESSOAIS
	dadosPessoais: Yup.object().shape({
		nomeAluno: Yup.string()
			.required(`Campo Obrigatório: Nome`)
			.test({
				name: 'onlyLetters',
				exclusive: true,
				message: 'Campo Nome: Apenas letras são permitidas.',
				test: value => onlyLetters(value),
			})
			.test({
				name: 'minCharacters',
				exclusive: true,
				message: 'Nome deve conter no mínimo 8 caracteres',
				test: value => minCharacters(8, value),
			})
			.test(
				'same-name-pai',
				'O nome não pode ser igual ao nome do pai.',
				function verify(value) {
					if (value) {
						return this.parent.nomePai !== value;
					}
					return true;
				},
			)
			.test(
				'same-name-mae',
				'O nome não pode ser igual ao nome da mãe.',
				function verify(value) {
					if (value) {
						return this.parent.nomeMae !== value;
					}
					return true;
				},
			),
		sexo: Yup.string().required(`Campo Obrigatório: Sexo`),
		email: Yup.string().email('O Email capturado está errado'),
		corRaca: Yup.string().required(`Campo Obrigatório: Raça/Cor`),
		dataNascimento: Yup.string().required(
			`Campo Obrigatório: Data de Nascimento`,
		),
		nomeMae: Yup.string().required(`Campo Obrigatório: Nome da Mãe`),
		nacionalidade: Yup.string().required(`Campo Obrigatório: Nacionalidade`),
		ufMunicipioNascimento: Yup.string().when('nacionalidade', {
			is: nacionalidade => nacionalidade === 1 || nacionalidade === '1',
			then: Yup.string().required(`Campo Obrigatório: UF de Nascimento`),
		}),
		nomeMunicipioNascimento: Yup.string().when('nacionalidade', {
			is: nacionalidade => nacionalidade === 1 || nacionalidade === '1',
			then: Yup.string().required(`Campo Obrigatório: Município de Nascimento`),
		}),
		dataEntradaPais: Yup.string().when(['nacionalidade'], {
			is: nacionalidade =>
				nacionalidade !== 1 && nacionalidade !== '1' && nacionalidade,
			then: Yup.string().required(`Campo Obrigatório: Data Entrada no Brasil`),
		}),
	}),

	// DOCUMENTOS
	documentos: Yup.object().shape(
		{
			numeroDocumentoCivil: Yup.string().when(
				['ufDocumentoCivil', 'dataEmissaoDocumentoCivil'],
				{
					is: (ufDocumentoCivil, dataEmissaoDocumentoCivil) =>
						(ufDocumentoCivil !== '' && ufDocumentoCivil !== 0) ||
						dataEmissaoDocumentoCivil !== '',
					then: Yup.string().required(`Campo Obrigatório: Documento Civil RG`),
				},
			),
			ufDocumentoCivil: Yup.string().when(
				['numeroDocumentoCivil', 'dataEmissaoDocumentoCivil'],
				{
					is: (numeroDocumentoCivil, dataEmissaoDocumentoCivil) =>
						numeroDocumentoCivil !== '' || dataEmissaoDocumentoCivil !== '',
					then: Yup.string().required(`Campo Obrigatório: UF RG`),
				},
			),
			dataEmissaoDocumentoCivil: Yup.string().when(
				['numeroDocumentoCivil', 'ufDocumentoCivil'],
				{
					is: (numeroDocumentoCivil, ufDocumentoCivil) =>
						numeroDocumentoCivil !== '' || ufDocumentoCivil !== '',
					then: Yup.string().required(`Campo Obrigatório: Data Emissão RG/RNE`),
				},
			),
			tipoCertidao: Yup.string().when(
				['numeroDocumentoCivil', 'cpf', 'numeroNIS'],
				{
					is: (numeroDocumentoCivil, cpf, numeroNIS) =>
						numeroDocumentoCivil === '' && cpf === '' && numeroNIS === '',
					then: Yup.string().required(`Necessário preencher um dos documentos`),
				},
			),
			dataEmissaoCin: Yup.string().when(['isCin'], {
				is: isCin => isCin === true,
				then: Yup.string().required(`Campo Obrigatório: Data Emissão CIN`),
			}),
		},
		[
			['numeroDocumentoCivil', 'ufDocumentoCivil'],
			['numeroDocumentoCivil', 'dataEmissaoDocumentoCivil'],
			['ufDocumentoCivil', 'dataEmissaoDocumentoCivil'],
		],
	),
	certidaoNova: Yup.object().shape({
		certificadoMatricula01: Yup.string().when(
			[
				'tipoCertidao',
				'certificadoMatricula02',
				'certificadoMatricula03',
				'certificadoMatricula04',
				'certificadoMatricula05',
				'certificadoMatricula06',
				'certificadoMatricula07',
				'certificadoMatricula08',
				'certificadoMatricula09',
			],
			{
				is: (
					tipoCertidao,
					certificadoMatricula02,
					certificadoMatricula03,
					certificadoMatricula04,
					certificadoMatricula05,
					certificadoMatricula06,
					certificadoMatricula07,
					certificadoMatricula08,
					certificadoMatricula09,
					certificadoMatricula01,
				) =>
					tipoCertidao === 'NOVA' &&
					(certificadoMatricula01 === '' ||
						certificadoMatricula02 === '' ||
						certificadoMatricula03 === '' ||
						certificadoMatricula04 === '' ||
						certificadoMatricula05 === '' ||
						certificadoMatricula06 === '' ||
						certificadoMatricula07 === '' ||
						certificadoMatricula08 === '' ||
						certificadoMatricula09 === ''),
				then: Yup.string().test({
					name: 'validation',
					exclusive: true,
					message: 'Campo Obrigatório: Matrícula',
					test: () => false,
				}),
			},
		),
		dataEmissaoCertidao: Yup.string().when(['tipoCertidao'], {
			is: tipoCertidao => tipoCertidao === 'NOVA',
			then: Yup.string().required(`Campo Obrigatório: Data de emissão`),
		}),
	}),
	certidaoAntiga: Yup.object().shape({
		dataEmissaoCertidao: Yup.string().when(['tipoCertidao'], {
			is: tipoCertidao => tipoCertidao === 'ANTIGA',
			then: Yup.string().required(`Campo Obrigatório: Data de emissão`),
		}),
		numeroCertidao: Yup.string().when(['tipoCertidao'], {
			is: tipoCertidao => tipoCertidao === 'ANTIGA',
			then: Yup.string().required(`Campo Obrigatório: Número`),
		}),
		numeroFolhaRegistro: Yup.string().when(['tipoCertidao'], {
			is: tipoCertidao => tipoCertidao === 'ANTIGA',
			then: Yup.string().required(`Campo Obrigatório: Folha`),
		}),
		numeroLivroRegistro: Yup.string().when(['tipoCertidao'], {
			is: tipoCertidao => tipoCertidao === 'ANTIGA',
			then: Yup.string().required(`Campo Obrigatório: Livro`),
		}),
		distritoNascimento: Yup.string().when(['tipoCertidao'], {
			is: tipoCertidao => tipoCertidao === 'ANTIGA',
			then: Yup.string().required(
				`Campo Obrigatório: Distrito Certidão Nascimento`,
			),
		}),
		nomeMunicipioComarca: Yup.string().when(['tipoCertidao'], {
			is: tipoCertidao => tipoCertidao === 'ANTIGA',
			then: Yup.string().required(`Campo Obrigatório: Município da Comarca`),
		}),
		ufComarca: Yup.string().when(['tipoCertidao'], {
			is: tipoCertidao => tipoCertidao === 'ANTIGA',
			then: Yup.string().required(`Campo Obrigatório: UF da Comarca`),
		}),
	}),

	// DEFICIÊNCIA
	deficiencia: Yup.object().shape({
		tipoCuidador: Yup.string().when(['cuidador'], {
			is: cuidador => cuidador === true,
			then: Yup.string().required(`Campo Obrigatório: Cuidador`),
		}),
		tipoProfSaude: Yup.string().when(['profSaude'], {
			is: profSaude => profSaude === true,
			then: Yup.string().required(`Campo Obrigatório: Profissional Saúde`),
		}),
		tipoMobilidadeReduzida: Yup.string().when(['mobilidadeReduzida'], {
			is: mobilidadeReduzida => mobilidadeReduzida === true,
			then: Yup.string().required(`Campo Obrigatório: Mobilidade Reduzida`),
		}),
		validacaoPossuiNecessidades: Yup.string().when(
			['possuiNecessidades', 'codigoNecessidade'],
			{
				is: (possuiNecessidades, codigoNecessidade) =>
					possuiNecessidades && codigoNecessidade === '',
				then: Yup.string().required(
					'Selecione uma necessidade especial disponível.',
				),
			},
		),
		profissionalApoioEscolarVO: Yup.object().shape({
			validaDatasVigenciaAtividadeDiaria: Yup.string().when(
				['dataInicioVigenciaAtividadeDiaria', 'dataFimVigenciaAtividadeDiaria'],
				{
					is: (
						dataInicioVigenciaAtividadeDiaria,
						dataFimVigenciaAtividadeDiaria,
					) => {
						return comparaData(
							dataInicioVigenciaAtividadeDiaria,
							dataFimVigenciaAtividadeDiaria,
							'maior',
						);
					},
					then: Yup.string().required(
						'A data início da Vigência para apoio Atividade de vida diária não pode ser maior que a data fim da Vigência para apoio Atividade de vida diária.',
					),
				},
			),
		}),
	}),

	recursoAvaliacao: Yup.object().shape({
		tamanhoFonte: Yup.string().when(['provaAmpliada'], {
			is: provaAmpliada => provaAmpliada === true,
			then: Yup.string().required(
				`Campo Obrigatório: Prova Ampliada (Tamanho da Fonte)`,
			),
		}),
	}),

	// ENDEREÇO RESIDENCIAL
	enderecoResidencial: Yup.object().shape({
		cep: Yup.string().required(`Campo Obrigatório: CEP`),
		areaLogradouro: Yup.string().required(
			`Campo Obrigatório: Localização/Zona de Residência`,
		),
		logradouro: Yup.string().required(`Campo Obrigatório: Endereço`),
		numero: Yup.string().required(`Campo Obrigatório: N°`),
		bairro: Yup.string().required(`Campo Obrigatório: Bairro`),
		nomeCidade: Yup.string().required(`Campo Obrigatório: Cidade - UF`),
		latitude: Yup.string().required(`Campo Obrigatório: Latitude`),
		longitude: Yup.string().required(`Campo Obrigatório: Longitude`),
	}),
	telefones: Yup.array().min(
		1,
		'Campo Obrigatório: Preencha pelo menos um número de telefone.',
	),
});

export const schemaTelefone = Yup.object<ITelefone>().shape({
	telefone: Yup.object().shape({
		descricaoTipo: Yup.string().required(`Campo Obrigatório: Tipo Telefone`),
		sms: Yup.string().when(['descricaoTipo'], {
			is: descricaoTipo => descricaoTipo === 'Celular',
			then: Yup.string().required(`Campo Obrigatório: Aceita SMS`),
		}),
		ddd: Yup.string().required(`Campo Obrigatório: DDD`),
		numero: Yup.string().required(`Campo Obrigatório: Número`),
	}),
});

export function formatValuesCreate(formValues: IFichaAluno): FichaAlunoEnvio {
	const {
		dadosPessoais,
		documentos,
		enderecoResidencial,
		deficiencia,
		recursoAvaliacao,
		certidaoAntiga,
		certidaoNova,
	} = formValues;

	const updatedDeficiencia: DeficienciaCadastroFichaAluno = {
		// codigoNecessidade: deficiencia.codigoNecessidade,
		codigoNecessidade: deficiencia.codigoNecessidade,
		// mobilidadeReduzida: !!deficiencia.mobilidadeReduzida,
		mobilidadeReduzida: deficiencia.mobilidadeReduzida || false,
		// tipoMobilidadeReduzida:
		// 	(deficiencia.mobilidadeReduzida && deficiencia.tipoMobilidadeReduzida) ||
		// 	'',
		tipoMobilidadeReduzida: deficiencia.tipoMobilidadeReduzida || '',
		// professorAuxiliar: !!deficiencia.professorAuxiliar || true,
		// dataInicioProfessorAuxiliar: unformatDate(deficiencia.dataInicioProfessorAuxiliar) || '',
		// dataFimProfessorAuxiliar: unformatDate(deficiencia.dataFimProfessorAuxiliar) || '',
		dataInicioMobilidadeReduzida:
			unformatDate(deficiencia.dataInicioMobilidadeReduzida) || '',
		dataFimMobilidadeReduzida:
			unformatDate(deficiencia.dataFimMobilidadeReduzida) || '',
		codigoNivelSuporte: deficiencia.codigoNivelSuporte,
		profissionalApoioEscolarVO: {
			profissionalApoioEscolar:
				deficiencia.profissionalApoioEscolarVO.profissionalApoioEscolar,
			atividadeDiaria: deficiencia.profissionalApoioEscolarVO.atividadeDiaria,
			atividadeEscolar: deficiencia.profissionalApoioEscolarVO.atividadeEscolar,
			apoioHigiene: deficiencia.profissionalApoioEscolarVO.apoioHigiene,
			apoioLocomocao: deficiencia.profissionalApoioEscolarVO.apoioLocomocao,
			apoioAlimentacao: deficiencia.profissionalApoioEscolarVO.apoioAlimentacao,
			apoioBanheiro: deficiencia.profissionalApoioEscolarVO.apoioBanheiro,
			apoioMedicamento: deficiencia.profissionalApoioEscolarVO.apoioMedicamento,
			dataInicioVigenciaAtividadeDiaria: unformatDate(
				deficiencia.profissionalApoioEscolarVO
					.dataInicioVigenciaAtividadeDiaria,
			), // '2022-01-01';
			dataFimVigenciaAtividadeDiaria: unformatDate(
				deficiencia.profissionalApoioEscolarVO.dataFimVigenciaAtividadeDiaria,
			), // '2023-01-01';
			dataInicioVigenciaAtividadeEscolar: unformatDate(
				deficiencia.profissionalApoioEscolarVO
					.dataInicioVigenciaAtividadeEscolar,
			), // '2022-01-01';
			dataFimVigenciaAtividadeEscolar: unformatDate(
				deficiencia.profissionalApoioEscolarVO.dataFimVigenciaAtividadeEscolar,
			), // '2023-01-01';
		},
	};

	function getFonteProva(tamanhoFonte: any, provaAmpliada: boolean): string {
		const fonte = tamanhoFonte?.toString();
		// eslint-disable-next-line no-nested-ternary
		return provaAmpliada && (fonte === '18' || fonte === '20')
			? fonte
			: provaAmpliada && fonte !== ''
				? fonte
				: '';
	}

	const updatedRecursoAvaliacao: RecursoAvaliacaoCadastroFichaAluno = {
		guiaInterprete: recursoAvaliacao.guiaInterprete,
		interpreteLibras: !!recursoAvaliacao.interpreteLibras,
		leituraLabial: !!recursoAvaliacao.leituraLabial,
		nenhum: !!recursoAvaliacao.nenhum,
		// provaAmpliada: !!(
		// 	!!recursoAvaliacao.provaAmpliada &&
		// 	(recursoAvaliacao.tamanhoFonte.toString() === '16' ||
		// 		recursoAvaliacao.tamanhoFonte.toString() === '24')
		// ),
		provaAmpliada: recursoAvaliacao.provaAmpliada,
		fonteProva: getFonteProva(
			recursoAvaliacao.tamanhoFonte,
			recursoAvaliacao.provaAmpliada,
		),
		provaBraile: !!recursoAvaliacao.provaBraile,
		auxilioTranscricao: !!recursoAvaliacao.auxilioTranscricao,
		auxilioLeitor: !!recursoAvaliacao.auxilioLeitor,
		provaVideoLibras: !!recursoAvaliacao.provaVideoLibras,
		provaLinguaPortuguesa: !!recursoAvaliacao.provaLinguaPortuguesa,
		cdAudioDefVisual: !!recursoAvaliacao.cdAudioDeficienteVisual,
		fonteProva18:
			recursoAvaliacao.provaAmpliada &&
			recursoAvaliacao.tamanhoFonte.toString() === '18',
		fonteProva20:
			recursoAvaliacao.provaAmpliada &&
			recursoAvaliacao.tamanhoFonte.toString() === '20',
	};

	const requestValues: FichaAlunoEnvio = {
		dadosPessoais: {
			nomeAluno: dadosPessoais.nomeAluno,
			nomeMae: dadosPessoais.nomeMae,
			nomePai: dadosPessoais.nomePai,
			nomeSocial: dadosPessoais.nomeSocial,
			nomeAfetivo: dadosPessoais.nomeAfetivo,
			dataNascimento:
				(dadosPessoais.dataNascimento?.includes('/') &&
					convertDate(dadosPessoais.dataNascimento)) ||
				dadosPessoais.dataNascimento ||
				'',
			codCorRaca: dadosPessoais.corRaca?.toString() || '',
			codSexo: dadosPessoais.codigoSexo?.toString() || '',
			email: dadosPessoais.email,
			codNacionalidade: dadosPessoais.nacionalidade?.toString() || '',
			nacionalidade: dadosPessoais.nacionalidade?.toString() || '',
			nomeMunicipioNascimento: dadosPessoais.nomeMunicipioNascimento,
			ufMunicipioNascimento: dadosPessoais.ufMunicipioNascimento,
			codPaisOrigem: dadosPessoais.codigoPaisOrigem?.toString() || '',
			paisOrigem: dadosPessoais.nomePaisOrigem?.toString() || '',
			codMunicipioNascimentoDNE:
				dadosPessoais.codigoMunicipioNascimentoDNE?.toString() || '',
			dataEntradaPais:
				(dadosPessoais.dataEntradaPais?.includes('/') &&
					convertDate(dadosPessoais.dataEntradaPais)) ||
				dadosPessoais.dataEntradaPais ||
				'',
			bolsaFamilia: !!dadosPessoais.codigoBolsaFamilia,
			quilombola: !!dadosPessoais.quilombola,
			possuiInternet: !!dadosPessoais.possuiInternet,
			possuiNotebookSmartphoneTablet:
				!!dadosPessoais.possuiNotebookSmartphoneTablet,
			tipoSanguineo: '',
			doadorOrgaos: false,
			flagSigilo: '',
			codTipoIndigena: dadosPessoais.codTipoIndigena.toString(),
		},
		documentos: {
			codigoINEP: documentos.codigoINEP?.toString() || '',
			cpf: limparMascara(documentos.cpf),
			numeroNis: limparMascara(documentos.numeroNIS),
			numDocumentoCivil: limparMascara(documentos.numeroDocumentoCivil),
			digitoDocumentoCivil: limparMascara(documentos.digitoDocumentoCivil),
			ufDocumentoCivil: documentos.ufDocumentoCivil,
			dataEmissaoDocumentoCivil:
				(documentos.dataEmissaoDocumentoCivil?.includes('/') &&
					convertDate(documentos.dataEmissaoDocumentoCivil)) ||
				documentos.dataEmissaoDocumentoCivil ||
				'',

			justificativaDocumentos: '',
			numeroCns: '',
			isCin: documentos.isCin,
			dataEmissaoCin:
				(documentos.dataEmissaoCin?.includes('/') &&
					convertDate(documentos.dataEmissaoCin)) ||
				documentos.dataEmissaoCin ||
				'',
		},
		enderecoResidencial: {
			codAreaLogradouro: enderecoResidencial.areaLogradouro || '',
			logradouro: enderecoResidencial.logradouro,
			numero: enderecoResidencial.numero,
			areaLogradouro: enderecoResidencial.areaLogradouro,
			complemento: enderecoResidencial.complemento,
			bairro: enderecoResidencial.bairro,
			cep: enderecoResidencial.cep,
			nomeCidade: enderecoResidencial.nomeCidade,
			ufCidade: enderecoResidencial.ufCidade,
			codMunicipioDNE: enderecoResidencial.codigoMunicipioDNE?.toString() || '',
			latitude: enderecoResidencial.latitude?.toString().trim() || '',
			longitude: enderecoResidencial.longitude?.toString().trim() || '',
			codLocalizacaoDiferenciada:
				enderecoResidencial.localizacaoDiferenciada?.toString() || '',
		},
		deficiencia: updatedDeficiencia,
		recursoAvaliacao: updatedRecursoAvaliacao,
		certidaoAntiga: {
			folha: certidaoAntiga.numeroFolhaRegistro,
			livro: certidaoAntiga.numeroLivroRegistro,
			numCertidao: certidaoAntiga.numeroCertidao,
			municipioComarca: certidaoAntiga.nomeMunicipioComarca,
			codMunicComarcaDNE: '',
			ufComarca: certidaoAntiga.ufComarca,
			distritoCertidao: certidaoAntiga.distritoNascimento,
			dataEmissaoCertidao:
				(certidaoAntiga.dataEmissaoCertidao?.includes('/') &&
					convertDate(certidaoAntiga.dataEmissaoCertidao)) ||
				certidaoAntiga.dataEmissaoCertidao ||
				'',
		},
		certidaoNova: {
			certMatr01: certidaoNova.certificadoMatricula01,
			certMatr02: certidaoNova.certificadoMatricula02,
			certMatr03: certidaoNova.certificadoMatricula03,
			certMatr04: certidaoNova.certificadoMatricula04,
			certMatr05: certidaoNova.certificadoMatricula05,
			certMatr06: certidaoNova.certificadoMatricula06,
			certMatr07: certidaoNova.certificadoMatricula07,
			certMatr08: certidaoNova.certificadoMatricula08,
			certMatr09: certidaoNova.certificadoMatricula09,
			dataEmissaoCertidao:
				(certidaoNova.dataEmissaoCertidao?.includes('/') &&
					convertDate(certidaoNova.dataEmissaoCertidao)) ||
				certidaoNova.dataEmissaoCertidao ||
				'',
		},
		rastreio: {
			usuarioRemoto: '',
			nomeUsuario: '',
			numCPF: '',
			localPerfilAcesso: '',
		},
	};

	if (certidaoNova.tipoCertidao === 'NOVA') {
		requestValues.certidaoAntiga = {
			folha: '',
			livro: '',
			numCertidao: '',
			municipioComarca: '',
			codMunicComarcaDNE: '',
			ufComarca: '',
			distritoCertidao: '',
			dataEmissaoCertidao: '',
		};
	} else if (certidaoAntiga.tipoCertidao === 'ANTIGA') {
		requestValues.certidaoNova = {
			certMatr01: '',
			certMatr02: '',
			certMatr03: '',
			certMatr04: '',
			certMatr05: '',
			certMatr06: '',
			certMatr07: '',
			certMatr08: '',
			certMatr09: '',
			dataEmissaoCertidao: '',
		};
	}

	return requestValues;
}

export function formatValuesUpdate(
	formValues: IFichaAluno,
): AlterarFichaAlunoRequestData {
	const { aluno, telefones } = formValues;

	const updatedTelefones: AlterarFichaAlunoTelefone[] =
		telefones?.map(telefone => ({
			dddNumero: telefone.ddd,
			numero: telefone.numero.toString() || '',
			tipoTelefone: telefone.tipo?.toString() || '',
			descricaoTipo: telefone.descricaoTipo?.toString() || '',
			complemento: telefone.complemento || '', // Added default value
			sms: !!telefone.sms,
		})) || [];

	const valuesRequestCreate = formatValuesCreate(formValues);

	const requestValues: AlterarFichaAlunoRequestData = {
		...valuesRequestCreate,
		aluno: {
			numRA: aluno.numeroRA,
			digitoRA: aluno.digitoRA,
			siglaUFRA: aluno.siglaUFRA,
		},
		dadosPessoais: {
			...valuesRequestCreate.dadosPessoais,
			dataNascimento:
				(valuesRequestCreate.dadosPessoais?.dataNascimento?.includes('/') &&
					convertDate(valuesRequestCreate.dadosPessoais.dataNascimento)) ||
				valuesRequestCreate.dadosPessoais.dataNascimento ||
				'',
			codNacionalidade: valuesRequestCreate.dadosPessoais.nacionalidade || '', // Ensure codNacionalidade is included
			// Add other required fields if needed
		},
		telefones: updatedTelefones,
	};

	return requestValues;
}
